/*globals showModalError, views, rulesRate, showModalConfirm*/

import React, {
  useState,
  useEffect,
  useCallback,
} from "react";
import styled from "styled-components";
import Request from "common/Request";
import DataTable, { Column } from "common/DataTable";
import { safeStr } from 'common/api';
import {parsePolicy, getPolicy, showDynPolicyRate} from "../../PolicyEdit/utils";

const doesNothing = () => {};

const returnView = "viewRulesRate";

/*****************************************************
 * Radius-Rest Policies View
 * ****************************************************/

export const SubscriberRatesPoliciesTable = () => {
  const [request, setRequest] = useState(null);

  const doRequest = () => ifCl.run("show policy rate full").then( response => ({response}));

  const doLoad = () => {
    setRequest(doRequest());
  };

  useEffect(() => doLoad(), []);

  return request === null ? null : (
    <div className="row clearfix">
      <div className="body table-responsive">
        <Request during={request}>
          {(result) =>  <ShowPolicies {...result} reload={doLoad} />}
        </Request>
      </div>
    </div>
  );
};

const notFoundRE = /%ERR-ENOENT: Cannot find "(.*)" policy/;

const doShowDynamicPolicy = (name) => {
  showDynPolicyRate(name);
};
/*****************************************************
 * Policies Table
 * ****************************************************/

const configPolicyActions = ([name, row], whenDone) => [
  {
    id: "edit-config",
    label: "Edit",
    icon: "edit",
    onClick: (_value, [name, _id, source, ..._rest]) => doEditPolicy(name, whenDone),
  },
  ...( 
    name === 'rate-default'
      ? []
      : [{
        id: "delete-config",
        label: "Delete",
        icon: "delete",
        disabled: name === 'rate-default',
        onClick: (_value, [name, _id, source, ..._rest]) => doDeletePolicy(name, whenDone),
      }]
  ),
];
const otherPolicyActions = (row, whenDone) => [
  {
    id: "view-others",
    label: "View",
    icon: "search",
    onClick: (_value, [name, ..._rest]) => doShowDynamicPolicy(name),
  },
  {
    id: "delete-others",
    label: "Delete",
    icon: "delete",
    onClick: (_value, [name, _id, source, ..._rest]) => doDeleteOtherPolicy(name, whenDone),
  }
];
const undefinedPolicyActions = (row) => [
  {
    id: "add-undefined",
    label: "Add",
    icon: "add_circle",
    onClick: (_value, [name, ..._rest]) => doAddPolicy(name),
  },
];

const TableContainer = styled.div`
  width: 100%;
  overflow: auto;
  margin-top: -20px;
`;
const ShowPolicies = ({ response, reload=doesNothing }) => {
  return (
  <>
    <TableContainer>
      <DataTable
        pageLength={50}
        exportAsCSV={true}
        content={response}
        columns={[
          Column.Text({
            label: "NAME",
            idx: 0,
            cellClassName: "hyperlink-text",
            onClick: (_, [name, _block, _limitDn, _limitIp, _flLimit, _acm, source] ) =>
               source === "static"
                ? doEditPolicy([name], reload)
                : source === "undefined"
                ? doAddPolicy(name, reload)
                : doShowDynamicPolicy(name, reload),

          }),
          Column.Text({
            label: "BLOCK",
            idx: 1,
          }),
          Column.SpeedOrNo({
            label: "RATE-LIMIT-DOWN",
            idx: 5,
            colClassName: "align-right",
          }),
          Column.SpeedOrNo({
            label: "RATE-LIMIT-UP",
            idx: 6,
            colClassName: "align-right",
          }),
          Column.Value(
            (flowLimit) => (flowLimit === 'n/a' || flowLimit === 'no') ? 'no': flowLimit,
            {
              label: "FLOW-LIMIT",
              idx: 7,
              colClassName: "align-right",
            }),
          Column.Text({
            label: "ACM",
            idx: 3,
          }),
          Column.Value(
            (source) => source === 'dynamic' ? 'no': 'yes',
            {
              label: "CONFIGURED",
              idx: 4,
            }),
          Column.Actions({
            label: "ACTIONS",
            idx: 4,
            are: (source, row) => 
              source === "static"
                ? configPolicyActions(row, reload)
                : source === "undefined"
                ? undefinedPolicyActions(row, reload)
                : otherPolicyActions(row, reload),
            colClassName: "text-center action",
            cellClassName: "hides-content-to-operators",
          }),
        ]}
      />
    </TableContainer>
  </>
)};


const doEditPolicy = (policy) => {
  globalNavigate("viewPolicyRateEdit", {returnView: "viewRulesRate", tab:2, policy})
};

const doAddPolicy = (policy) => {
  getPolicy()
    .then((_resp) => {
      showModalError("Error:", "Already defined policy " + policy);
    })
    .catch((error) => {
      const notFound = error.match(notFoundRE);
      if (notFound !== null) {
        const [_all, missingName, ..._rest] = notFound;
        return { missing: missingName };
      }
      showModalError("Error:", error);
    })
    .then(({ missing }) => {
      if (missing !== undefined) {
        globalNavigate("viewPolicyRateEdit", {returnView: "viewRulesRate", tab:2, policy})
      } else {
        console.error("missing policy to create is missing");
      }
    });
};

const doDeletePolicy = (name, whenDone=doesNothing) =>
  showModalConfirm(
    `Remove "${name}" policy?`,
    'WARNING: All subscribers assigned to this policy will be reassigned to a default rate policy following the BQN configured rules (normally default policy is "rate-default").',
    "delete_forever",
    function () {
      window.setTimeout(function () {
        //TODO Fix this code
        ifCl.execute(`clear policy rate ${safeStr(name)}`, function (resp) {
          var response = resp.CmdResponse.response;
          if (
            response.length == 0 ||
            response.indexOf("Conflict between ") > -1
          ) {
            whenDone();
          } else {
            showModalError("Warning:", resp.CmdResponse.response);
          }
        });
      }, 32);
    }
  );

  const doDeleteOtherPolicy = (name, whenDone=doesNothing) =>
    showModalConfirm(
      `Remove "${name}" policy?`,
      'WARNING: All subscribers assigned to this policy will be reassigned to a default rate policy following the BQN configured rules (normally default policy is "rate-default").',
      "delete_forever",
      function () {
        window.setTimeout(function () {
          //TODO Fix this code
          ifCl.execute(`clear api policy ${safeStr(name)}`, function (resp) {
            var response = resp.CmdResponse.response;
            if (
              response.length == 0 ||
              response.indexOf("Conflict between ") > -1
            ) {
              whenDone();
            } else {
              showModalError("Warning:", resp.CmdResponse.response);
            }
          });
        }, 32);
      }
    );

const doOpenActiveSubscribers = (name, row) => {
  const activeSubs = row.at(8);
  if (Number(activeSubs) > 0) {
    view.doKeep(returnView);
    globalNavigate("viewStatusSubscribers", { returnView, policy: name });
  }
};
